import { History, Maximize2, Minimize2, SquarePen, ArrowLeft, Pin, PinOff } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useAskSidebar } from "../../chat/AskContext";
import { useChatSession } from "../../chat/chatStore";
import { useChatDraft } from "../../chat/chatDraft";
import { questionChatKey } from "../../watchlist/recentQuestions";

/** Starting a new chat and browsing past ones. */
export function AskConversationActions() {
  const { t } = useTranslation();
  const { conversationId, watchRequest, history, setHistory, openQuestion } = useAskSidebar();
  const session = useChatSession(questionChatKey(conversationId));
  const [draft] = useChatDraft(questionChatKey(conversationId));
  const canReturn = Boolean(draft.trim() || session.entries.length || watchRequest);
  return (
    <>
      <button
        type="button"
        onClick={() => {
          openQuestion({});
          setHistory(false);
        }}
        title={t("chat_new_question")}
        aria-label={t("chat_new_question")}
        className="inline-flex h-8 shrink-0 items-center justify-center gap-1.5 rounded-lg px-2 text-xs text-info transition-colors hover:bg-muted-surface focus-visible:outline-info"
      >
        <SquarePen size={17} aria-hidden="true" />
        <span className="max-sm:sr-only">{t("askPanel.new_chat")}</span>
      </button>
      {(!history || canReturn) && (
        <button
          type="button"
          onClick={() => setHistory(!history)}
          aria-expanded={history}
          className="inline-flex min-h-8 items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm text-text-mid transition-colors hover:bg-muted-surface hover:text-info focus-visible:outline-info"
        >
          {history ? <ArrowLeft size={15} /> : <History size={15} />}
          <span className="max-sm:sr-only">
            {t(history ? "chat_back_to_conversation" : "chat_recent")}
          </span>
        </button>
      )}
    </>
  );
}

/** Where the panel sits: docked beside the page or over it, or expanded to fill it. */
export function AskPanelActions() {
  const { t } = useTranslation();
  const { expanded, docked, toggleDocked, toggleExpanded } = useAskSidebar();
  return (
    <>
      {!expanded && (
        <button
          type="button"
          onClick={toggleDocked}
          aria-pressed={docked}
          aria-label={t(docked ? "askPanel.overlay" : "askPanel.dock")}
          title={t(docked ? "askPanel.overlay" : "askPanel.dock")}
          className={`hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg hover:bg-muted-surface focus-visible:outline-info lg:inline-flex ${docked ? "bg-info/10 text-info" : "text-text-mid"}`}
        >
          {docked ? <PinOff size={17} aria-hidden="true" /> : <Pin size={17} aria-hidden="true" />}
        </button>
      )}
      <button
        type="button"
        onClick={toggleExpanded}
        title={t(expanded ? "chat_collapse" : "chat_expand")}
        aria-label={t(expanded ? "chat_collapse" : "chat_expand")}
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-mid transition-colors hover:bg-muted-surface hover:text-info focus-visible:outline-info"
      >
        {expanded ? <Minimize2 size={17} /> : <Maximize2 size={17} />}
      </button>
    </>
  );
}
